import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/apiClient";
import { AGENTS } from "../data/agents";
import AgentIcon from "../components/AgentIcon";
import { ArrowRight, ArrowLeft, Sparkles, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import CreditEstimateDialog from "../components/CreditEstimateDialog";
import { DocumentUpload } from "../components/DocumentUpload";
import { useT } from "../lib/i18n";

const Field = ({ label, children, hint }) => (
  <div className="flex flex-col gap-2">
    <label className="text-sm font-bold text-[#0B1B3D] font-display">{label}</label>
    {children}
    {hint && <p className="text-xs text-[#94A3B8] font-mono-kz">{hint}</p>}
  </div>
);

const inputCls = "border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-[#0EA5E9] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20 bg-white";

export default function CreateSession() {
  const { t } = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: params.get("template") || "",
    description: "",
    industry: "",
    company_size: "Small (1-50)",
    country: "",
    urgency: "standard",
    desired_outcome: "",
    confidentiality: "standard",
    agent_ids: (params.get("agents") || "").split(",").filter(Boolean),
    rounds: 3,
    depth: "standard",
    output_format: "executive_summary",
    risk_sensitivity: "medium",
    documents: [],
  });

  const setDocuments = (updater) => setForm((f) => ({ ...f, documents: typeof updater === "function" ? updater(f.documents) : updater }));

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAgent = (id) => setForm((f) => ({ ...f, agent_ids: f.agent_ids.includes(id) ? f.agent_ids.filter((x) => x !== id) : [...f.agent_ids, id] }));

  const canStep2 = form.title.trim().length > 3 && form.description.trim().length > 10;
  const canStep3 = form.agent_ids.length > 0;

  const [estimateOpen, setEstimateOpen] = useState(false);

  const openEstimate = () => setEstimateOpen(true);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/sessions", form);
      toast.success("Session created. Running Konzenzo rounds…");
      navigate(`/sessions/${r.data.session_id}/workspace?run=1`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create session");
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout title={t("Create a decision session")}>
      <div className="max-w-4xl">
        <div className="flex items-center gap-2 mb-8 font-mono-kz text-xs tracking-widest uppercase">
          {[
            { n: 1, t: t("Context") },
            { n: 2, t: t("Agents") },
            { n: 3, t: t("Setup") },
          ].map((s, i) => (
            <React.Fragment key={s.n}>
              <span className={`flex items-center gap-2 ${step === s.n ? "text-[#0B1B3D]" : "text-[#94A3B8]"}`}>
                <span className={`w-6 h-6 rounded-full grid place-items-center font-bold ${step >= s.n ? "bg-[#0B1B3D] text-white" : "bg-slate-200 text-[#475569]"}`}>{s.n}</span>
                {s.t}
              </span>
              {i < 2 && <span className="text-[#CBD5E1]">———</span>}
            </React.Fragment>
          ))}
        </div>

        {step === 1 && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 grid md:grid-cols-2 gap-6">
            <div className="md:col-span-2"><Field label={t("Business challenge title")}>
              <input data-testid="input-title" className={inputCls} value={form.title} onChange={(e) => upd("title", e.target.value)} placeholder="e.g., How can we reduce VAT impact in our bakery?" />
            </Field></div>
            <div className="md:col-span-2"><Field label={t("Describe the situation")} hint={t("Provide as much business context as possible.")}>
              <textarea data-testid="input-description" rows={5} className={inputCls} value={form.description} onChange={(e) => upd("description", e.target.value)} placeholder="Annual revenue, products, current pain, jurisdictions, deadlines…" />
            </Field></div>
            <Field label={t("Industry")}><input data-testid="input-industry" className={inputCls} value={form.industry} onChange={(e) => upd("industry", e.target.value)} placeholder="Food & beverage" /></Field>
            <Field label={t("Country / jurisdiction")}><input data-testid="input-country" className={inputCls} value={form.country} onChange={(e) => upd("country", e.target.value)} placeholder="Germany" /></Field>
            <Field label={t("Company size")}>
              <select data-testid="input-size" className={inputCls} value={form.company_size} onChange={(e) => upd("company_size", e.target.value)}>
                {["Solo / Founder", "Small (1-50)", "Mid (51-250)", "Large (250+)"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <Field label={t("Urgency")}>
              <select data-testid="input-urgency" className={inputCls} value={form.urgency} onChange={(e) => upd("urgency", e.target.value)}>
                {["low", "standard", "high"].map((s) => <option key={s} value={s}>{t(s)}</option>)}
              </select>
            </Field>
            <div className="md:col-span-2"><Field label={t("Desired outcome")}>
              <input data-testid="input-outcome" className={inputCls} value={form.desired_outcome} onChange={(e) => upd("desired_outcome", e.target.value)} placeholder="A 3-step plan to legally reduce VAT impact next quarter" />
            </Field></div>
            <Field label={t("Confidentiality")}>
              <select data-testid="input-confidentiality" className={inputCls} value={form.confidentiality} onChange={(e) => upd("confidentiality", e.target.value)}>
                {["standard", "sensitive", "highly_confidential"].map((s) => <option key={s} value={s}>{t(s.replace("_", " "))}</option>)}
              </select>
            </Field>
            <div className="md:col-span-2"><Field label={t("Business documents (optional)")} >
              <DocumentUpload documents={form.documents} onChange={setDocuments} />
            </Field></div>
            <div className="md:col-span-2 flex justify-end gap-3 mt-2">
              <button data-testid="step-1-next" disabled={!canStep2} onClick={() => setStep(2)} className="bg-[#0B1B3D] disabled:opacity-40 text-white hover:bg-[#1E3A8A] px-6 py-3 rounded-lg font-bold font-display inline-flex items-center gap-2">{t("Next: select agents")} <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white border border-slate-200 rounded-xl p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-display font-extrabold text-[#0B1B3D] text-xl">{t("Select your expert panel")}</h2>
                <p className="text-sm text-[#475569] mt-1">{t("Pick at least one. Recommended panels coming next iteration.")}</p>
              </div>
              <span className="font-mono-kz text-xs text-[#0EA5E9] tracking-widest font-bold">{form.agent_ids.length} {t("SELECTED")}</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {AGENTS.map((a) => {
                const sel = form.agent_ids.includes(a.id);
                return (
                  <button key={a.id} data-testid={`select-agent-${a.id}`} onClick={() => toggleAgent(a.id)} className={`text-left flex flex-col gap-3 p-5 rounded-xl border transition-all ${sel ? "border-[#0EA5E9] ring-2 ring-[#0EA5E9]/20 bg-[#0EA5E9]/5" : "border-slate-200 hover:border-[#0EA5E9]"}`}>
                    <div className="flex items-center justify-between">
                      <div className="w-9 h-9 rounded-md bg-[#0B1B3D] grid place-items-center text-white"><AgentIcon name={a.icon} className="w-4 h-4" /></div>
                      {sel && <span className="font-mono-kz text-xs text-[#10B981] font-bold tracking-widest">SELECTED</span>}
                    </div>
                    <p className="font-display font-extrabold text-[#0B1B3D]">{a.name}</p>
                    <p className="text-xs text-[#0EA5E9] font-bold uppercase tracking-wider font-mono-kz">{a.specialty}</p>
                    <p className="text-sm text-[#475569]">{a.when_to_use}</p>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-between mt-8">
              <button data-testid="step-2-back" onClick={() => setStep(1)} className="text-sm font-bold text-[#0B1B3D] inline-flex items-center gap-2 hover:underline"><ArrowLeft className="w-4 h-4" /> {t("Back")}</button>
              <button data-testid="step-2-next" disabled={!canStep3} onClick={() => setStep(3)} className="bg-[#0B1B3D] disabled:opacity-40 text-white hover:bg-[#1E3A8A] px-6 py-3 rounded-lg font-bold font-display inline-flex items-center gap-2">{t("Next: setup")} <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 grid md:grid-cols-2 gap-6">
            <Field label={t("Number of Konzenzo rounds")}>
              <select data-testid="input-rounds" className={inputCls} value={form.rounds} onChange={(e) => upd("rounds", Number(e.target.value))}>
                <option value={1}>{t("1 round (quick)")}</option>
                <option value={2}>{t("2 rounds (standard)")}</option>
                <option value={3}>{t("3 rounds (deep)")}</option>
              </select>
            </Field>
            <Field label={t("Depth of analysis")}>
              <select data-testid="input-depth" className={inputCls} value={form.depth} onChange={(e) => upd("depth", e.target.value)}>
                {["quick", "standard", "deep"].map((s) => <option key={s} value={s}>{t(s)}</option>)}
              </select>
            </Field>
            <Field label={t("Output format")}>
              <select data-testid="input-output" className={inputCls} value={form.output_format} onChange={(e) => upd("output_format", e.target.value)}>
                <option value="executive_summary">{t("Executive summary")}</option>
                <option value="detailed_report">{t("Detailed report")}</option>
                <option value="action_plan">{t("Action plan")}</option>
              </select>
            </Field>
            <Field label={t("Risk sensitivity")}>
              <select data-testid="input-risk" className={inputCls} value={form.risk_sensitivity} onChange={(e) => upd("risk_sensitivity", e.target.value)}>
                {["low", "medium", "high"].map((s) => <option key={s} value={s}>{t(s)}</option>)}
              </select>
            </Field>
            <div className="md:col-span-2 rounded-lg bg-[#F8FAFC] border border-slate-200 p-5 flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-[#F59E0B] mt-0.5" />
              <p className="text-sm text-[#475569]">{t("Konzenzo agents provide structured analysis to support — not replace — qualified human review for tax, legal and financial matters.")}</p>
            </div>
            <div className="md:col-span-2 flex justify-between">
              <button data-testid="step-3-back" onClick={() => setStep(2)} className="text-sm font-bold text-[#0B1B3D] inline-flex items-center gap-2 hover:underline"><ArrowLeft className="w-4 h-4" /> {t("Back")}</button>
              <button data-testid="submit-session" disabled={submitting} onClick={openEstimate} className="bg-[#10B981] disabled:opacity-50 text-white hover:bg-[#059669] px-6 py-3 rounded-lg font-bold font-display inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> {t("Review credit cost")}
              </button>
            </div>
          </div>
        )}
        <CreditEstimateDialog
          open={estimateOpen}
          onClose={() => setEstimateOpen(false)}
          onConfirm={() => { setEstimateOpen(false); submit(); }}
          num_agents={form.agent_ids.length}
          rounds={form.rounds}
          output_format={form.output_format}
          has_documents={form.documents.length > 0}
        />
      </div>
    </DashboardLayout>
  );
}
